import { useState, type FormEvent } from "react";
import { API_ROUTES, LIMITS, type Survey } from "@ziolo/shared";
import { Button, Card, useFlash } from "@ziolo/ui";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { useFormSubmit } from "../hooks/useFormSubmit.js";
import { useSurveys } from "../hooks/useSurveys.js";
import { useSession } from "../session/SessionProvider.js";
import { formatPercent, pluralVotes } from "../lib/format.js";
import { Field } from "./Field.js";

/** The sidebar: create a survey, then vote in the open ones. */
export function SurveysPanel() {
  const { user } = useSession();
  const surveys = useSurveys();

  return (
    <Card variant="panel" title="Ankiety" icon="📊">
      {user ? <SurveyForm onCreated={surveys.add} /> : null}

      {surveys.error ? <p className="hub-error">{surveys.error}</p> : null}

      {surveys.loading ? (
        <p className="hub-hint">Wczytywanie ankiet...</p>
      ) : surveys.items.length === 0 ? (
        <div className="hub-surveys__empty">
          <p>Brak aktywnych ankiet</p>
          {user ? <p className="hub-hint">Bądź pierwszy i utwórz ankietę!</p> : null}
        </div>
      ) : (
        <div className="hub-surveys">
          {surveys.items.map((survey) => (
            <SurveyItem
              key={survey.id}
              survey={survey}
              onVote={surveys.vote}
              onDelete={surveys.remove}
            />
          ))}
        </div>
      )}
    </Card>
  );
}

const MIN_OPTIONS = LIMITS.surveyOptions.min;
const MAX_OPTIONS = LIMITS.surveyOptions.max;

function SurveyForm({ onCreated }: { onCreated: (survey: Survey) => void }) {
  const { busy, fields, submit } = useFormSubmit();
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState<string[]>(() =>
    Array.from({ length: MIN_OPTIONS }, () => ""),
  );

  function setOption(index: number, value: string) {
    setOptions((current) =>
      current.map((option, position) => (position === index ? value : option)),
    );
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();

    const ok = await submit(async () => {
      const created = await apiRequest<Survey>(API_ROUTES.surveys.create, {
        method: "POST",
        body: { question, options },
      });
      onCreated(created);
    }, "Ankieta została utworzona");

    if (ok) {
      setQuestion("");
      setOptions(Array.from({ length: MIN_OPTIONS }, () => ""));
    }
  }

  return (
    <form className="hub-form hub-survey-form" onSubmit={onSubmit}>
      <h4 className="hub-subtitle">Stwórz ankietę</h4>

      <Field error={fields.question}>
        <textarea
          className="z-textarea"
          name="question"
          placeholder="Pytanie..."
          maxLength={LIMITS.surveyQuestion.max}
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          required
        />
      </Field>

      <Field error={fields.options}>
        <div className="hub-survey-form__options">
          {options.map((option, index) => (
            // Index keys are correct here: the rows are positional, and a row's
            // identity is its position in the option list.
            <div className="hub-survey-form__row" key={index}>
              <input
                className="z-input"
                placeholder={`Opcja ${index + 1}...`}
                maxLength={LIMITS.surveyOption.max}
                value={option}
                onChange={(event) => setOption(index, event.target.value)}
                {...(index < MIN_OPTIONS ? { required: true } : {})}
              />
              {options.length > MIN_OPTIONS ? (
                <button
                  type="button"
                  className="hub-icon-btn"
                  aria-label={`Usuń opcję ${index + 1}`}
                  onClick={() =>
                    setOptions((current) =>
                      current.filter((_, position) => position !== index),
                    )
                  }
                >
                  ×
                </button>
              ) : null}
            </div>
          ))}
        </div>
      </Field>

      <div className="hub-form__actions">
        <Button
          size="sm"
          variant="ghost"
          disabled={options.length >= MAX_OPTIONS}
          onClick={() => setOptions((current) => [...current, ""])}
        >
          + Dodaj opcję
        </Button>
        <Button type="submit" variant="primary" size="sm" disabled={busy}>
          {busy ? "Tworzenie..." : "Utwórz ankietę"}
        </Button>
      </div>
    </form>
  );
}

interface SurveyItemProps {
  survey: Survey;
  onVote: (surveyId: number, optionId: number) => Promise<void>;
  onDelete: (surveyId: number) => Promise<void>;
}

function SurveyItem({ survey, onVote, onDelete }: SurveyItemProps) {
  const { push } = useFlash();
  const [selected, setSelected] = useState<number | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(action: () => Promise<void>) {
    setBusy(true);
    try {
      await action();
    } catch (cause) {
      push(errorMessage(cause), "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <article className="hub-survey">
      <header>
        <h4 className="hub-survey__question">{survey.question}</h4>
        <div className="hub-survey__meta">
          <span>przez {survey.creatorUsername ?? "Anonim"}</span>
          <span>{pluralVotes(survey.totalVotes)}</span>
        </div>
      </header>

      {survey.hasVoted ? (
        <div className="hub-results">
          {survey.options.map((option) => {
            const percent = formatPercent(option.voteCount, survey.totalVotes);
            const mine = option.id === survey.votedOptionId;

            return (
              <div
                className={`hub-result ${mine ? "hub-result--mine" : ""}`}
                key={option.id}
              >
                <div className="hub-result__text">
                  {option.text}
                  {mine ? <span className="hub-result__badge">Twój głos</span> : null}
                </div>
                <div className="hub-result__bar">
                  <div className="hub-result__fill" style={{ width: `${percent}%` }} />
                  <span className="hub-result__value">
                    {percent}% ({option.voteCount})
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <form
          className="hub-vote-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (selected === null) return;
            void run(() => onVote(survey.id, selected));
          }}
        >
          <div className="hub-vote-options">
            {survey.options.map((option) => (
              <label className="hub-vote-option" key={option.id}>
                <input
                  type="radio"
                  name={`survey-${survey.id}`}
                  value={option.id}
                  checked={selected === option.id}
                  onChange={() => setSelected(option.id)}
                  required
                />
                <span>{option.text}</span>
              </label>
            ))}
          </div>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            block
            disabled={busy || selected === null}
          >
            {busy ? "Zapisywanie..." : "Głosuj"}
          </Button>
        </form>
      )}

      {survey.canDelete ? (
        <div className="hub-survey__admin">
          <Button
            variant="danger"
            size="sm"
            disabled={busy}
            onClick={() => {
              if (!window.confirm("Czy na pewno chcesz usunąć tę ankietę?")) return;
              void run(() => onDelete(survey.id));
            }}
          >
            Usuń
          </Button>
        </div>
      ) : null}
    </article>
  );
}
